import { createSelector } from 'reselect';

import { EMPTY_MARKETPLACE_METADATA, MARKETPLACE_DEFAULT_ALLOWED_COUNTRIES } from '../../constants/marketplace';
import { IPickedMskuItem } from '../../types/api';
import { ClaimSourceEnum, IVariantOptionRow, MarketplaceDetailsStatusEnum } from '../../types/marketplace';
import { ICampaign } from '../../types/pyg';
import { IAppState } from '../../types/store';
import { canAffordCredits, getCatalogBudget, getRemainingCredits, isProductInCart } from '../../util/credits';
import {
  areAllVariantsOutOfStock,
  buildCatalogContext,
  buildVariantOptionRows,
  collectMarketplaceProductIds,
  getCampaignMarketplaceProductIds,
  getPickableVariants,
  getSelectedFilters,
  getSupplierLock,
  hasFilterValues,
  isDetailsSettled,
  isMarketplaceId,
  isOtherSupplier,
  isSameCatalogContext,
  needsOptionsStep,
  stripMarketplacePrefix,
} from '../../util/marketplace';
import { selectPickedCampaignId, selectPickedItems } from './form';
import { selectCampaigns, selectItems, selectRootState } from './init';

const selectMarketplaceState = (state: IAppState) => state.marketplace;

export const selectMarketplaceItems = createSelector(selectMarketplaceState, (marketplace) => marketplace.items);

export const selectMarketplaceCatalogItem = (productId?: string) =>
  createSelector(selectMarketplaceItems, (items) => items.find((item) => item.product_id === productId));

export const selectMarketplaceMetadata = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.metadata ?? EMPTY_MARKETPLACE_METADATA,
);

export const selectIsMetadataLoaded = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.metadata !== null,
);

export const selectMarketplaceFilters = createSelector(selectMarketplaceState, (marketplace) => marketplace.filters);

export const selectMarketplaceNextCursor = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.nextCursor,
);

export const selectIsMarketplaceLoading = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.isLoading,
);

export const selectIsMarketplaceLoadingMore = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.isLoadingMore,
);

export const selectMarketplaceError = createSelector(selectMarketplaceState, (marketplace) => marketplace.error);

export const selectMarketplaceCacheValue = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.cacheValue,
);

export const selectMarketplaceCart = createSelector(selectMarketplaceState, (marketplace) => marketplace.cart);

export const selectSuspendedCart = createSelector(selectMarketplaceState, (marketplace) => marketplace.suspendedCart);

export const selectSupplierLock = createSelector(selectMarketplaceCart, getSupplierLock);

export const selectCanFilterBySupplier = createSelector(selectSupplierLock, (lock) => !!lock?.code);

export const selectIsSupplierFilterOn = createSelector(
  selectMarketplaceState,
  selectCanFilterBySupplier,
  (marketplace, canFilterBySupplier) => marketplace.isSupplierFilterOn && canFilterBySupplier,
);

export const selectVisibleMarketplaceItems = createSelector(
  selectMarketplaceItems,
  selectIsSupplierFilterOn,
  selectSupplierLock,
  (items, isSupplierFilterOn, lock) =>
    isSupplierFilterOn ? items.filter((item) => !isOtherSupplier(item.supplierCode, lock)) : items,
);

export const selectReplacedCart = createSelector(selectMarketplaceState, (marketplace) => marketplace.replacedCart);

export const selectHasActiveMarketplaceFilters = createSelector(
  selectMarketplaceFilters,
  selectIsSupplierFilterOn,
  (filters, isSupplierFilterOn) =>
    !!filters.query || hasFilterValues(filters) || filters.fitsBudget || isSupplierFilterOn,
);

export const selectMarketplaceDetailsMap = createSelector(selectMarketplaceState, (marketplace) => marketplace.details);

const selectMarketplaceDetailsStatusMap = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.detailsStatus,
);

// Cart lines carry only what the picks list renders, so the selection card and details drawer
// read the description from the fetched details.
export const selectMarketplaceDescriptionFn = createSelector(
  selectMarketplaceDetailsMap,
  (details) =>
    (productId: string): string | undefined =>
      details[productId]?.description,
);

export const selectMarketplaceProductDetails = (productId?: string) =>
  createSelector(selectMarketplaceDetailsMap, (details) => (productId ? details[productId] : undefined));

export const selectMarketplaceProductStatus = (productId?: string) =>
  createSelector(selectMarketplaceDetailsStatusMap, (statusMap) =>
    productId ? statusMap[productId] ?? MarketplaceDetailsStatusEnum.Idle : MarketplaceDetailsStatusEnum.Idle,
  );

export const selectOpenProductId = createSelector(selectMarketplaceState, (marketplace) => marketplace.openProductId);

export const selectIsCartOpen = createSelector(selectMarketplaceState, (marketplace) => marketplace.isCartOpen);

export const selectSwapPending = createSelector(selectMarketplaceState, (marketplace) => marketplace.swapPending);

export const selectGiftBudget = createSelector(selectRootState, (root) => root.giftBudget ?? null);

export const selectHasGiftBudget = createSelector(selectGiftBudget, (giftBudget) => !!giftBudget?.enabled);

export const selectCreditsRemaining = createSelector(selectMarketplaceCart, getRemainingCredits);

export const selectCartCount = createSelector(selectMarketplaceCart, (cart) => cart.length);

const selectMarketplaceCursorBudget = createSelector(selectMarketplaceState, (marketplace) => marketplace.cursorBudget);

export const selectCatalogBudget = createSelector(selectMarketplaceFilters, selectMarketplaceCart, (filters, cart) =>
  getCatalogBudget(filters.fitsBudget, cart),
);

const selectLiveCatalogContext = createSelector(selectMarketplaceFilters, selectMarketplaceCart, buildCatalogContext);

const selectRequestedCatalogContext = createSelector(
  selectMarketplaceState,
  (marketplace) => marketplace.catalogContext,
);

export const selectResultCatalogContext = createSelector(
  selectRequestedCatalogContext,
  selectLiveCatalogContext,
  (requested, live) => requested ?? live,
);

export const selectIsCatalogResultStale = createSelector(
  selectRequestedCatalogContext,
  selectLiveCatalogContext,
  (requested, live) => !!requested && !isSameCatalogContext(requested, live),
);

export const selectSelectedMarketplaceFilters = createSelector(
  selectMarketplaceFilters,
  selectMarketplaceMetadata,
  getSelectedFilters,
);

export const selectResultFilterName = createSelector(
  selectResultCatalogContext,
  selectMarketplaceMetadata,
  (context, metadata) => {
    const selected = getSelectedFilters(context, metadata);

    return selected.length === 1 ? selected[0].label : '';
  },
);

export const selectHasNextPage = createSelector(
  selectMarketplaceNextCursor,
  selectMarketplaceCursorBudget,
  selectCatalogBudget,
  selectIsMarketplaceLoading,
  (cursor, cursorBudget, budget, isLoading) => !!cursor && cursorBudget === budget && !isLoading,
);

// Other-supplier items can only replace the cart, never join it, so they do not count as fitting.
export const selectAffordableItemCount = createSelector(
  selectMarketplaceItems,
  selectMarketplaceCart,
  selectSupplierLock,
  (items, cart, lock) =>
    items.filter(
      (item) =>
        !isProductInCart(item.product_id, cart) &&
        !isOtherSupplier(item.supplierCode, lock) &&
        canAffordCredits(item.creditPrice, cart),
    ).length,
);

// The catalog arrives page by page, so "nothing still fits" is only safe once the cursor is
// exhausted; otherwise the notice would announce closure on page one and contradict itself.
const selectHasSeenWholeCatalog = createSelector(selectMarketplaceNextCursor, (cursor) => !cursor);

export const selectNothingElseFits = createSelector(
  selectAffordableItemCount,
  selectHasSeenWholeCatalog,
  (affordableCount, hasSeenWholeCatalog) => hasSeenWholeCatalog && affordableCount === 0,
);

export const selectCatalogCampaigns = createSelector(selectCampaigns, (campaigns) =>
  (campaigns ?? []).filter((campaign) => !isMarketplaceId(campaign.id)),
);

export const selectClaimSource = createSelector(
  selectPickedCampaignId,
  selectMarketplaceCart,
  selectItems,
  (pickedCampaignId, cart, items): ClaimSourceEnum => {
    if (cart.length > 0) {
      return ClaimSourceEnum.MarketplaceBudget;
    }

    if (pickedCampaignId) {
      return isMarketplaceId(pickedCampaignId) ? ClaimSourceEnum.MarketplacePreselected : ClaimSourceEnum.Catalog;
    }

    return (items ?? []).some((item) => isMarketplaceId(item.id))
      ? ClaimSourceEnum.MarketplacePreselected
      : ClaimSourceEnum.None;
  },
);

const selectIsMarketplaceClaim = createSelector(
  selectClaimSource,
  (source) => source === ClaimSourceEnum.MarketplaceBudget || source === ClaimSourceEnum.MarketplacePreselected,
);

export const selectMarketplaceSubmitLines = createSelector(selectMarketplaceCart, (cart) =>
  cart.map(({ product_id, variant_id }) => ({ product_id, variant_id })),
);

export const selectAutoPickedVariantIdFn = createSelector(
  selectMarketplaceDetailsMap,
  (detailsMap) =>
    (itemId?: string): string | null => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return null;
      }
      const details = detailsMap[stripMarketplacePrefix(itemId)];
      const pickable = details ? getPickableVariants(details).filter((variant) => !variant.isOutOfStock) : [];

      return pickable.length === 1 ? pickable[0].variant_id : null;
    },
);

// Undefined for IE-catalog claims: no country restriction exists there and none is added.
export const selectAllowedCountries = createSelector(
  selectIsMarketplaceClaim,
  selectRootState,
  (isMarketplaceClaim, root): string[] | undefined =>
    isMarketplaceClaim
      ? root.shippingRules?.marketplace_allowed_countries ?? MARKETPLACE_DEFAULT_ALLOWED_COUNTRIES
      : undefined,
);

export const selectCheckingProductId = createSelector(
  selectMarketplaceDetailsStatusMap,
  selectOpenProductId,
  (statusMap, openProductId) => {
    const entry = Object.entries(statusMap).find(
      ([productId, status]) => status === MarketplaceDetailsStatusEnum.Loading && productId !== openProductId,
    );

    return entry?.[0] ?? null;
  },
);

// Bare product ids for every marketplace item in the send, direct or nested in a campaign.
export const selectMarketplaceProductIdsToHydrate = createSelector(
  selectCampaigns,
  selectItems,
  (campaigns, items): string[] => {
    const campaignIds = (campaigns ?? []).flatMap(getCampaignMarketplaceProductIds);

    return Array.from(new Set([...campaignIds, ...collectMarketplaceProductIds(items ?? [])]));
  },
);

export const selectMarketplaceProductIdsToLoad = createSelector(
  selectMarketplaceProductIdsToHydrate,
  selectMarketplaceDetailsMap,
  selectMarketplaceDetailsStatusMap,
  (productIds, detailsMap, statusMap): string[] =>
    productIds.filter((productId) => !detailsMap[productId] && !statusMap[productId]),
);

const findCampaign = (campaigns: ICampaign[] | null, campaignId?: string | null) =>
  campaigns?.find(({ id }) => id === campaignId);

export const selectMarketplaceCampaignProductIds = (campaignId?: string | null) =>
  createSelector(selectCampaigns, (campaigns): string[] => {
    const campaign = findCampaign(campaigns, campaignId);

    return campaign ? getCampaignMarketplaceProductIds(campaign) : [];
  });

// Whether a fetch for this campaign is already running, so callers join it instead of
// requesting the same products twice.
export const selectIsMarketplaceCampaignLoadingFn = createSelector(
  selectMarketplaceDetailsStatusMap,
  selectCampaigns,
  (statusMap, campaigns) =>
    (campaignId?: string | null): boolean => {
      const campaign = findCampaign(campaigns, campaignId);
      const productIds = campaign ? getCampaignMarketplaceProductIds(campaign) : [];

      return productIds.some((productId) => statusMap[productId] === MarketplaceDetailsStatusEnum.Loading);
    },
);

// Every product of the campaign has settled — loaded or failed — so a partly failed campaign
// still opens its sidebar instead of hanging.
export const selectIsMarketplaceCampaignSettledFn = createSelector(
  selectMarketplaceDetailsStatusMap,
  selectCampaigns,
  (statusMap, campaigns) =>
    (campaignId?: string | null): boolean => {
      const campaign = findCampaign(campaigns, campaignId);
      const productIds = campaign ? getCampaignMarketplaceProductIds(campaign) : [];

      return !!productIds.length && productIds.every((productId) => isDetailsSettled(statusMap[productId]));
    },
);

// Whether the current step is showing marketplace items. By the time the options step renders,
// a picked campaign's items have been copied into inventory_items.
export const selectHasMarketplaceItems = createSelector(selectItems, (items) =>
  (items ?? []).some((item) => isMarketplaceId(item.id)),
);

// Predicates rather than per-row selectors: a selector factory called inside a .map() builds a
// new selector each render and loses memoization.
export const selectMarketplaceItemStatusFn = createSelector(
  selectMarketplaceDetailsStatusMap,
  (statusMap) =>
    (itemId?: string): MarketplaceDetailsStatusEnum => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return MarketplaceDetailsStatusEnum.Idle;
      }

      return statusMap[stripMarketplacePrefix(itemId)] ?? MarketplaceDetailsStatusEnum.Idle;
    },
);

export const selectNeedsMarketplaceOptionsStepFn = createSelector(
  selectMarketplaceDetailsMap,
  (detailsMap) =>
    (itemId?: string): boolean => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return false;
      }
      const details = detailsMap[stripMarketplacePrefix(itemId)];

      return !!details && needsOptionsStep(details);
    },
);

export const selectIsMarketplaceItemOutOfStockFn = createSelector(
  selectMarketplaceDetailsMap,
  (detailsMap) =>
    (itemId?: string): boolean => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return false;
      }
      const details = detailsMap[stripMarketplacePrefix(itemId)];

      return !!details && areAllVariantsOutOfStock(details);
    },
);

export const selectMarketplaceItemImagesFn = createSelector(
  selectMarketplaceDetailsMap,
  (detailsMap) =>
    (itemId?: string): string[] => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return [];
      }

      return detailsMap[stripMarketplacePrefix(itemId)]?.images ?? [];
    },
);

export const selectMarketplaceItemOptionRowsFn = createSelector(
  selectMarketplaceDetailsMap,
  (detailsMap) =>
    (itemId?: string): IVariantOptionRow[] => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return [];
      }
      const details = detailsMap[stripMarketplacePrefix(itemId)];

      return details ? buildVariantOptionRows(details.variants) : [];
    },
);

// The vendor comes from the list payload, so it paints before any details are fetched; the
// details and catalog copies are fallbacks for items reached without a campaign entry.
export const selectMarketplaceItemVendorFn = createSelector(
  selectMarketplaceState,
  selectCampaigns,
  selectItems,
  (marketplace, campaigns, items) =>
    (itemId?: string): string | undefined => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return undefined;
      }
      const productId = stripMarketplacePrefix(itemId);
      const campaignItems = (campaigns ?? []).flatMap((campaign) => campaign.items ?? []);
      const listVendor = [...campaignItems, ...(items ?? [])].find((item) => item.id === itemId)?.vendor;

      return (
        listVendor ??
        marketplace.details[productId]?.vendor ??
        marketplace.items.find((item) => item.product_id === productId)?.vendor
      );
    },
);

// Blocks Continue: a card still loading may yet reveal variants that need picking, and a failed
// card would otherwise submit a claim with no variant chosen. An item already carrying options is
// resolved whatever its status says, which covers a back-navigation onto hydrated state.
export const selectIsMarketplaceItemResolvedFn = createSelector(
  selectMarketplaceDetailsMap,
  selectMarketplaceDetailsStatusMap,
  (detailsMap, statusMap) =>
    (itemId?: string): boolean => {
      if (!itemId || !isMarketplaceId(itemId)) {
        return false;
      }
      const productId = stripMarketplacePrefix(itemId);

      return !!detailsMap[productId] || statusMap[productId] === MarketplaceDetailsStatusEnum.Loaded;
    },
);

export const selectHasUnresolvedMarketplaceItems = createSelector(
  selectItems,
  selectIsMarketplaceItemResolvedFn,
  (items, isResolved) =>
    (items ?? []).some((item) => {
      if (!isMarketplaceId(item.id) || !!item.sku_options?.length) {
        return false;
      }

      return !isResolved(item.id);
    }),
);

export const selectMarketplaceSubmitPicks = createSelector(
  selectItems,
  selectPickedItems,
  selectAutoPickedVariantIdFn,
  selectIsMarketplaceItemOutOfStockFn,
  (items, pickedItems, autoPickedVariantIdOf, isItemOutOfStock): IPickedMskuItem[] => {
    const lines = new Map<string, IPickedMskuItem>();

    (items ?? []).forEach((item) => {
      if (!isMarketplaceId(item.id) || isItemOutOfStock(item.id) || lines.has(item.id)) {
        return;
      }
      const uid = pickedItems[item.internalId]?.uid ?? autoPickedVariantIdOf(item.id);

      if (uid) {
        lines.set(item.id, { parent_id: item.id, uid, quantity: 1 });
      }
    });

    return Array.from(lines.values());
  },
);

export const selectHasMissingVariantPicks = createSelector(
  selectClaimSource,
  selectItems,
  selectMarketplaceSubmitPicks,
  selectHasUnresolvedMarketplaceItems,
  selectIsMarketplaceItemOutOfStockFn,
  (claimSource, items, submitPicks, hasUnresolvedItems, isItemOutOfStock): boolean => {
    if (claimSource !== ClaimSourceEnum.MarketplacePreselected) {
      return false;
    }
    const submittedIds = new Set(submitPicks.map((line) => line.parent_id));

    return (
      hasUnresolvedItems ||
      (items ?? []).some((item) => isMarketplaceId(item.id) && !isItemOutOfStock(item.id) && !submittedIds.has(item.id))
    );
  },
);
